import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { api, imageSrc } from '../services/api'
import { RESOURCES } from './resources'
import { mergeDevices } from '../hooks/useSiteData'

const MAX_MB = 2
const TYPES = ['image/jpeg', 'image/png', 'image/webp']
const VIDEO_ACCEPT = 'video/*,.mp4,.webm,.mov,.m4v'
const isVideoFile = (f) => f.type.startsWith('video/') || /\.(mp4|webm|mov|m4v)$/i.test(f.name)
const MAX_VIDEO_MB = 50
const isFile = (f) => f.type === 'image' || f.type === 'images'
const isVideo = (f) => f.type === 'video'
const textFields = (cfg) => cfg.fields.filter((f) => !isFile(f) && !isVideo(f))

// Preview of the chosen / saved video. If the browser cannot play it, the admin is told
// straight away (before customers ever see a broken player).
function VideoPreview({ src }) {
  const [bad, setBad] = useState(false)
  useEffect(() => { setBad(false) }, [src])
  return (
    <div>
      <video key={src} src={src} controls preload="metadata" onError={() => setBad(true)} />
      {bad && (
        <p className="adm__error" role="alert">
          This video cannot be played in the browser. Please convert it to MP4 (H.264) and upload it again.
        </p>
      )}
    </div>
  )
}

const defaults = (cfg) =>
  Object.fromEntries(textFields(cfg).map((f) => [f.key, f.type === 'checkbox' ? f.def !== false : f.def ?? '']))

// the images an item already has: an `images` list if the server sends one, otherwise the single image_url
const imgList = (it) => {
  let list = it.images
  if (typeof list === 'string') { try { list = JSON.parse(list) } catch { list = null } }
  if (Array.isArray(list) && list.length) return list.filter(Boolean)
  return it.image_url ? [it.image_url] : []
}

export default function AdminResource({ name }) {
  const cfg = RESOURCES[name]
  if (!cfg) return <Navigate to="/admin" replace />
  return <Manager key={name} name={name} cfg={cfg} />
}

function TagInput({ label, help, note, placeholder, emptyText, value, onChange, suggestions = [] }) {
  const id = useId()
  const [draft, setDraft] = useState('')
  const list = String(value || '').split(',').map((s) => s.trim()).filter(Boolean)
  const save = (arr) => onChange(arr.join(', '))

  const add = () => {
    const fresh = draft.split(',').map((s) => s.trim()).filter(Boolean)
    setDraft('')
    if (!fresh.length) return
    const seen = new Set(list.map((s) => s.toLowerCase()))
    const next = [...list]
    fresh.forEach((s) => { if (!seen.has(s.toLowerCase())) { seen.add(s.toLowerCase()); next.push(s) } })
    save(next)
  }

  const same = (a, b) => a.toLowerCase() === b.toLowerCase()
  const has = (m) => list.some((x) => same(x, m))
  const toggle = (m) => save(has(m) ? list.filter((x) => !same(x, m)) : [...list, m])
  const allOn = suggestions.length > 0 && suggestions.every(has)
  const selectAll = () => save([...list, ...suggestions.filter((m) => !has(m))])
  const clearBrand = () => save(list.filter((x) => !suggestions.some((m) => same(m, x))))

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add() }
    else if (e.key === 'Backspace' && !draft && list.length) save(list.slice(0, -1))
  }

  return (
    <div className="adm__field adm__wide">
      <label htmlFor={id}>{label}</label>
      <div className="adm__tagadd">
        <input id={id} type="text" placeholder={placeholder} value={draft}
          onChange={(e) => setDraft(e.target.value)} onKeyDown={onKeyDown} onBlur={add} />
        <button type="button" className="adm__btn adm__btn--ghost" onClick={add}>Add</button>
      </div>
      {help && <span className="adm__help">{help}</span>}
      {note && <span className="adm__help">{note}</span>}
      {suggestions.length > 0 && (
        <div className="adm__pick" role="group" aria-label="Phone models">
          <div className="adm__pickhead">
            <span>Tap to add or remove</span>
            <button type="button" className="adm__linkbtn" onClick={allOn ? clearBrand : selectAll}>{allOn ? 'Clear all' : 'Select all'}</button>
          </div>
          <div className="adm__pickgrid">
            {suggestions.map((m) => (
              <button type="button" key={m} className="adm__opt" aria-pressed={has(m)} onClick={() => toggle(m)}>{m}</button>
            ))}
          </div>
        </div>
      )}
      {list.length ? (
        <ol className="adm__tags">
          {list.map((m, i) => (
            <li key={m}>
              <span className="adm__tagno">{i + 1}</span>
              {m}
              <button type="button" onClick={() => save(list.filter((_, j) => j !== i))} aria-label={`Remove ${m}`}>×</button>
            </li>
          ))}
        </ol>
      ) : (
        <span className="adm__help adm__tags-empty">{emptyText || 'No models added, so this fits all phones.'}</span>
      )}
    </div>
  )
}

function QuickPanel({ fields, item, onSave, onClose }) {
  const start = (f) => item[f.key] ?? f.def ?? ''
  const [v, setV] = useState(() => Object.fromEntries(fields.map((f) => [f.key, start(f)])))
  const [busy, setBusy] = useState(false)
  const changes = Object.fromEntries(fields.filter((f) => String(v[f.key]) !== String(start(f))).map((f) => [f.key, v[f.key]]))
  const dirty = Object.keys(changes).length > 0
  return (
    <div className="adm__quicks">
      {fields.map((f) => (
        <label key={f.key}>{f.label}
          {f.options ? (
            <select value={v[f.key]} onChange={(e) => setV({ ...v, [f.key]: e.target.value })}>
              {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input type="number" min="0" placeholder={f.placeholder} value={v[f.key]} onChange={(e) => setV({ ...v, [f.key]: e.target.value })} />
          )}
        </label>
      ))}
      <button type="button" className="adm__btn" disabled={!dirty || busy}
        onClick={async () => { setBusy(true); const ok = await onSave(changes); setBusy(false); if (ok) onClose() }}>
        {busy ? 'Saving…' : 'Save'}
      </button>
      <button type="button" className="adm__btn adm__btn--ghost" onClick={onClose}>Cancel</button>
    </div>
  )
}

function Manager({ name, cfg }) {
  const imgField = cfg.fields.find(isFile)
  const multi = imgField?.type === 'images'
  const maxFiles = multi ? imgField.max || 6 : 1

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [values, setValues] = useState(defaults(cfg))
  const [editId, setEditId] = useState(null)
  const [picked, setPicked] = useState([])      // new files: [{ file, url }]
  const [existing, setExisting] = useState([])  // images the item already has (urls from the server)
  const [videoPicked, setVideoPicked] = useState(null)   // new video: { file, url }
  const [existingVideo, setExistingVideo] = useState('') // video the item already has (url from the server)
  const [removeVideo, setRemoveVideo] = useState(false)  // admin chose to delete the saved video
  const [saving, setSaving] = useState(false)
  const [lists, setLists] = useState({})   // other admin sections this form picks from (categories, brands)
  const [q, setQ] = useState('')
  const [quickId, setQuickId] = useState(null) // item whose price & stock panel is open
  // every brand with its models: the defaults in site.js plus the ones added under Phone brands
  const brandMap = useMemo(() => mergeDevices(lists.brands || []), [lists.brands])

  
  const load = useCallback(async () => {
    try {
      const response = await api.list(name, true)

      // Accept the supported API response formats safely.
      let data = []

      if (Array.isArray(response)) {
        data = response
      } else if (Array.isArray(response?.data)) {
        data = response.data
      } else if (Array.isArray(response?.items)) {
        data = response.items
      }

      // Sort safely, even if the server returns an unexpected response.
      const sortedItems = [...data].sort(
        (a, b) =>
          (Number(a?.sort_order) || 0) -
          (Number(b?.sort_order) || 0)
      )

      setItems(sortedItems)
      setError('')
    } catch (e) {
      console.error(`Failed to load ${name}:`, e)
      setItems([])
      setError(e.message || 'Failed to load data. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [name])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    const sources = new Set(cfg.fields.map((f) => f.optionsFrom).filter(Boolean))
    if (cfg.fields.some((f) => f.suggestionsFrom)) sources.add('brands') // model suggestions come from the chosen brand
    sources.forEach((s) => api.list(s, true).then((d) => setLists((l) => ({ ...l, [s]: d }))).catch(() => {}))
  }, [cfg])

  // free the temporary preview URLs when leaving the page
  const pickedRef = useRef(picked)
  pickedRef.current = picked
  useEffect(() => () => pickedRef.current.forEach((p) => URL.revokeObjectURL(p.url)), [])
  const videoRef = useRef(videoPicked)
  videoRef.current = videoPicked
  useEffect(() => () => { if (videoRef.current) URL.revokeObjectURL(videoRef.current.url) }, [])

  const clearPicked = () => { picked.forEach((p) => URL.revokeObjectURL(p.url)); setPicked([]) }

  const clearVideoState = () => {
    if (videoPicked) URL.revokeObjectURL(videoPicked.url)
    setVideoPicked(null); setExistingVideo(''); setRemoveVideo(false)
  }

  const reset = () => {
    setValues(defaults(cfg)); setEditId(null); clearPicked(); setExisting([]); clearVideoState()
  }

  const pick = (e) => {
    const list = Array.from(e.target.files || [])
    e.target.value = '' // so the same file can be chosen again; chosen images show below as thumbnails
    if (!list.length) return

    const room = multi ? maxFiles - existing.length - picked.length : 1
    if (room <= 0) return setError(`You can add up to ${maxFiles} images. Remove one to add another.`)

    let msg = ''
    const ok = []
    for (const f of list) {
      if (!TYPES.includes(f.type)) { msg = 'Use JPG, PNG or WebP images.'; continue }
      if (f.size > MAX_MB * 1024 * 1024) { msg = `Each image must be under ${MAX_MB} MB.`; continue }
      ok.push(f)
    }
    if (ok.length > room) msg = `Only ${maxFiles} images are allowed, so the extra ones were skipped.`
    setError(msg)

    const items = ok.slice(0, room).map((file) => ({ file, url: URL.createObjectURL(file) }))
    if (!items.length) return
    if (multi) setPicked([...picked, ...items])
    else { clearPicked(); setPicked(items) }
  }

  const pickVideo = (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    if (!isVideoFile(f)) return setError('Use an MP4, WebM or MOV video.')
    if (f.size > MAX_VIDEO_MB * 1024 * 1024) return setError(`The video must be under ${MAX_VIDEO_MB} MB.`)
    setError('')
    if (videoPicked) URL.revokeObjectURL(videoPicked.url)
    setVideoPicked({ file: f, url: URL.createObjectURL(f) }); setRemoveVideo(false)
  }
  const dropVideo = () => {
    if (videoPicked) URL.revokeObjectURL(videoPicked.url)
    setVideoPicked(null); setRemoveVideo(!!existingVideo)
  }

  const removeNew = (i) => {
    URL.revokeObjectURL(picked[i].url)
    setPicked(picked.filter((_, j) => j !== i))
  }
  const removeOld = (i) => setExisting(existing.filter((_, j) => j !== i))

  const edit = (it) => {
    const v = {}
    textFields(cfg).forEach((f) => { v[f.key] = f.type === 'checkbox' ? !!Number(it[f.key]) : f.type === 'select' && !it[f.key] ? f.def ?? '' : it[f.key] ?? '' })
    setValues(v); setEditId(it.id); clearPicked(); setExisting(imgList(it)); clearVideoState(); setExistingVideo(it.video_url || '')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const submit = async (e) => {
    e.preventDefault()
    for (const f of textFields(cfg)) {
      if (f.required && !String(values[f.key]).trim()) return setError(`${f.label} is required.`)
    }
    const problem = cfg.validate?.(values)
    if (problem) return setError(problem)

    const total = multi ? existing.length + picked.length : picked.length || existing.length
    if (imgField?.required && !total) return setError(multi ? 'Please add at least one image.' : 'Please choose an image.')

    const fd = new FormData()
    textFields(cfg).forEach((f) => fd.append(f.key, f.type === 'checkbox' ? (values[f.key] ? 1 : 0) : String(values[f.key]).trim()))
    if (multi) {
      picked.forEach((p) => fd.append('images', p.file))
      // tells the server which old images to keep; anything not listed is deleted
      if (editId) fd.append('keep_images', JSON.stringify(existing))
    } else if (picked[0]) {
      fd.append('image', picked[0].file)
    }
    if (videoPicked) fd.append('video', videoPicked.file)
    else if (editId && removeVideo) fd.append('remove_video', '1') // tells the server to delete the saved video

    setSaving(true); setError('')
    try {
      editId ? await api.update(name, editId, fd) : await api.create(name, fd)
      reset(); await load()
    } catch (err) { setError(err.message) }
    finally { setSaving(false) }
  }

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it[cfg.title]}"? This cannot be undone.`)) return
    try { await api.remove(name, it.id); await load() } catch (err) { setError(err.message) }
  }

  // change a few fields (price, sale price, stock status) straight from the list, keeping everything else as it is.
  // No image fields and no keep_images are sent, so the server leaves the images untouched.
  const quickSave = async (it, changes) => {
    const problem = cfg.validate?.({ ...it, ...changes })
    if (problem) { setError(problem); return false }
    const fd = new FormData()
    textFields(cfg).forEach((f) => fd.append(f.key, f.type === 'checkbox' ? (Number(it[f.key]) ? 1 : 0) : String(it[f.key] ?? '')))
    Object.entries(changes).forEach(([k, val]) => fd.set(k, val))
    try { await api.update(name, it.id, fd); setError(''); await load(); return true } catch (err) { setError(err.message); return false }
  }

  const setVal = (k, v) => setValues((s) => ({ ...s, [k]: v }))

  // the video shown under its file picker: a newly chosen one, or the one already saved
  const videoUrl = videoPicked ? videoPicked.url : existingVideo && !removeVideo ? imageSrc(existingVideo) : ''

  // thumbnails shown under the file picker
  const tiles = multi
    ? [
        ...existing.map((url, i) => ({ src: imageSrc(url), remove: () => removeOld(i) })),
        ...picked.map((p, i) => ({ src: p.url, remove: () => removeNew(i) })),
      ]
    : picked.length
      ? [{ src: picked[0].url, remove: () => removeNew(0) }]
      : existing.slice(0, 1).map((url) => ({ src: imageSrc(url) }))

  const field = (f) => {
    if (isVideo(f))
      return (
        <Fragment key={f.key}>
          <label className="adm__wide">
            {f.label} (MP4, WebM or MOV, max {MAX_VIDEO_MB} MB)
            <input type="file" accept={VIDEO_ACCEPT} onChange={pickVideo} />
            {f.help && <span className="adm__help">{f.help} Large videos can take a little while to save.</span>}
          </label>
          {videoUrl && (
            <div className="adm__wide adm__videoprev">
              <VideoPreview src={videoUrl} />
              <button type="button" className="adm__btn adm__btn--ghost" onClick={dropVideo}>Remove video</button>
            </div>
          )}
        </Fragment>
      )
    if (isFile(f))
      return (
        <Fragment key={f.key}>
          <label className={multi ? 'adm__wide' : undefined}>
            {f.label} (JPG, PNG or WebP, max {MAX_MB} MB{multi ? ' each' : ''})
            <input type="file" accept={TYPES.join(',')} multiple={multi} onChange={pick} />
            {multi && (
              <span className="adm__help">
                Select several at once, or add more later. The first image is the main one shown in the shop. {existing.length + picked.length} of {maxFiles} used.
              </span>
            )}
          </label>
          {tiles.length > 0 && (
            <div className="adm__wide adm__thumbs">
              {tiles.map((t, i) => (
                <figure className="adm__thumb" key={`${t.src}-${i}`}>
                  <img src={t.src} alt={`Preview ${i + 1}`} />
                  {multi && i === 0 && <figcaption>Main</figcaption>}
                  {t.remove && (
                    <button type="button" className="adm__thumb-x" onClick={t.remove} aria-label={`Remove image ${i + 1}`}>×</button>
                  )}
                </figure>
              ))}
            </div>
          )}
        </Fragment>
      )
    if (f.type === 'checkbox')
      return (
        <label key={f.key} className="adm__check">
          <input type="checkbox" checked={!!values[f.key]} onChange={(e) => setVal(f.key, e.target.checked)} />
          {f.label}
        </label>
      )
    if (f.type === 'textarea')
      return (
        <label key={f.key} className="adm__wide">{f.label}
          <textarea rows={3} value={values[f.key]} onChange={(e) => setVal(f.key, e.target.value)} />
        </label>
      )
    if (f.type === 'select') {
      let opts = f.optionsFrom === 'brands' ? Object.keys(brandMap) : f.options || (lists[f.optionsFrom] || []).map((c) => c.name)
      // keep the saved value selectable even if it was removed from the list
      if (values[f.key] && !opts.includes(values[f.key])) opts = [...opts, values[f.key]]
      const change = (e) => {
        const v = e.target.value
        setValues((s) => ({ ...s, [f.key]: v, ...Object.fromEntries((f.clears || []).map((k) => [k, ''])) }))
      }
      return (
        <label key={f.key}>{f.label}
          <select value={values[f.key]} onChange={change}>
            {!f.noEmpty && <option value="">{f.emptyLabel || (opts.length ? 'Choose…' : 'Add a category first')}</option>}
            {opts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {f.help && <span className="adm__help">{f.help}</span>}
        </label>
      )
    }
    if (f.type === 'tags')
      return (
        <TagInput key={f.key} label={f.label} help={f.help} placeholder={f.placeholder}
          emptyText={f.empty}
          suggestions={f.suggestionsFrom ? brandMap[values[f.suggestionsFrom]] || [] : []}
          note={f.suggestionsFrom && !values[f.suggestionsFrom] ? 'Choose a brand above to pick from its models, or type your own.' : ''}
          value={values[f.key]} onChange={(v) => setVal(f.key, v)} />
      )
    const note = f.hint?.(values) || f.help
    return (
      <label key={f.key}>{f.label}
        <input
          type={f.type === 'number' ? 'number' : 'text'}
          min={f.min} max={f.type === 'number' ? f.max : undefined} maxLength={f.type === 'text' ? f.max : undefined}
          placeholder={f.placeholder} value={values[f.key]} onChange={(e) => setVal(f.key, e.target.value)}
        />
        {note && <span className="adm__help">{note}</span>}
      </label>
    )
  }

  const needle = q.trim().toLowerCase()
  const visible = needle
    ? items.filter((it) => `${it[cfg.title]} ${it.category || ''} ${it.brand || ''}`.toLowerCase().includes(needle))
    : items
  const hasActive = cfg.fields.some((f) => f.key === 'is_active')
  const quick = cfg.quick ? [].concat(cfg.quick) : []

  return (
    <>
      <h1>{cfg.label}</h1>
      <form className="adm__card adm__form" onSubmit={submit}>
        <h2>{editId ? `Edit ${cfg.singular}` : `Add ${cfg.singular}`}</h2>
        <div className="adm__grid">{cfg.fields.map(field)}</div>
        {error && <p className="adm__error" role="alert">{error}</p>}
        <div className="adm__row">
          <button className="adm__btn" disabled={saving}>{saving ? 'Saving…' : editId ? 'Save changes' : `Add ${cfg.singular}`}</button>
          {editId && <button type="button" className="adm__btn adm__btn--ghost" onClick={reset}>Cancel</button>}
        </div>
      </form>

      {loading ? <p>Loading…</p> : !items.length ? <p className="adm__hint">Nothing here yet. Add your first {cfg.singular} above.</p> : (
        <>
        {items.length > 5 && (
          <input className="adm__search" type="search" placeholder={`Search ${cfg.label.toLowerCase()}`} value={q} onChange={(e) => setQ(e.target.value)} />
        )}
        <ul className="adm__list">
          {visible.map((it) => {
            const cover = imgList(it)[0]
            const badge = cfg.badge?.(it)
            const flag = cfg.flag?.(it)
            return (
              <li key={it.id} className={`adm__card adm__item${imgField ? '' : ' adm__item--plain'}`}>
                {imgField && (cover ? <img src={imageSrc(cover)} alt="" /> : <div className="adm__noimg">No image</div>)}
                <div>
                  <strong>{it[cfg.title]}</strong>
                  {hasActive && <span className={Number(it.is_active) ? 'adm__tag' : 'adm__tag adm__tag--off'}>{Number(it.is_active) ? 'Visible' : 'Hidden'}</span>}
                  {flag && <span className={`adm__tag ${flag === 'Out of stock' ? 'adm__tag--out' : 'adm__tag--soon'}`}>{flag}</span>}
                  {badge && <span className="adm__tag adm__tag--sale">{badge}</span>}
                  {cfg.sub && <div className="adm__hint">{cfg.sub(it)}</div>}
                  {quick.length > 0 && quickId === it.id && (
                    <QuickPanel fields={quick} item={it} onSave={(ch) => quickSave(it, ch)} onClose={() => setQuickId(null)} />
                  )}
                </div>
                <div className="adm__row">
                  <button className="adm__btn adm__btn--ghost" onClick={() => edit(it)}>Edit</button>
                  {quick.length > 0 && (
                    <button className="adm__btn adm__btn--ghost" onClick={() => setQuickId(quickId === it.id ? null : it.id)} aria-expanded={quickId === it.id}>
                      {cfg.quickLabel || 'Quick edit'}
                    </button>
                  )}
                  <button className="adm__btn adm__btn--danger" onClick={() => remove(it)}>Delete</button>
                </div>
              </li>
            )
          })}
        </ul>
        {!visible.length && <p className="adm__hint">No matches.</p>}
        </>
      )}
    </>
  )
}